import {isUuid} from '@benchling/js-utils';

import {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import {JSONValue} from 'util/Types';

function isStringArray(jsonValue: JSONValue): jsonValue is Array<string> {
  return Array.isArray(jsonValue) && jsonValue.every((arrayElement) => typeof arrayElement === 'string');
}

/**
 * Extract ids from cell, using stringIsIdFn predicate.
 *
 *   - If cell has only ids, then return them in an array.
 *   - Otherwise, return null.
 */
export function cellToIds(
  cell: JSONValue,
  stringIsIdFn: (stringValue: string) => boolean
): Array<string> | null {
  if (typeof cell === 'string' && stringIsIdFn(cell)) {
    return [cell];
  }
  if (isStringArray(cell) && cell.every((stringValue) => stringIsIdFn(stringValue))) {
    return cell;
  }
  return null;
}

/**
 * Converts a cell value to an array of item API ids, if possible.
 *
 * If the cell value is an item API id or an array of API ids, the ids are returned as an array of strings.
 * Otherwise, null is returned.
 */
export function cellToChipItemIds(cell: JSONValue): Array<string> | null {
  return cellToIds(cell, (value) => typeof value === 'string' && isModelBenchChipSupported(value));
}

/**
 * Converts a cell value to an array of blob UUIDs, if possible.
 *
 * If the cell value is a blob UUID or an array of blob UUIDs, the IDs are returned as an array of strings.
 * Otherwise, null is returned.
 *
 * UUIDs can be used for many things beside blobs, so this should only be called if context dictates that
 * the values are meant to be blob IDs.
 */
export function cellToBlobIds(cell: JSONValue): Array<string> | null {
  return cellToIds(cell, (value) => isUuid(value));
}
